import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R$ ${(v ?? 0).toFixed(2).replace('.', ',')}`;

export default function PaginaOK() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [planId, setPlanId] = useState(null);
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ok/${token}`)
      .then(r => { setInfo(r.data); setPlanId(r.data.recommended_plan); })
      .catch(err => setError(err.response?.data?.detail || 'Link inválido ou expirado.'));
  }, [token]);

  const confirm = async () => {
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/ok/${token}/confirm`, { plan_id: planId, cpf_cnpj: cpf });
      setDone(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao confirmar. Tente novamente.');
    } finally { setLoading(false); }
  };

  const selectedPlan = info?.plans?.find(p => p.id === planId);
  const priceOf = (p) => p.custom_price ?? p.price;
  const inputCls = "w-full px-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-slate-400 text-base focus:outline-none focus:ring-2 focus:ring-[#C5A059]";

  return (
    <div className="min-h-screen bg-[#0A2647] flex flex-col items-center justify-center p-5 py-10" data-testid="ok-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <p className="text-4xl mb-2">⭐⭐⭐⭐⭐</p>
          <h1 className="text-3xl font-bold text-[#C5A059]">Falo Com o Dono</h1>
        </div>

        {!info && !error && (
          <p className="text-slate-300 text-center" data-testid="ok-loading">Carregando...</p>
        )}

        {error && !info && (
          <div className="bg-white/10 border border-red-400/40 rounded-2xl p-8 text-center" data-testid="ok-error">
            <p className="text-4xl mb-3">😕</p>
            <p className="text-white">{error}</p>
          </div>
        )}

        {info && (info.already_active || info.ok_confirmed) && !done && (
          <div className="bg-white/10 border border-[#C5A059]/40 rounded-2xl p-8 text-center" data-testid="ok-already-active">
            <p className="text-5xl mb-4">✅</p>
            <h2 className="text-xl font-bold text-white mb-2">{info.company_name} já está ativa!</h2>
            <p className="text-slate-300 text-sm">Seu plano já foi confirmado. O boleto está no seu <b className="text-[#C5A059]">WhatsApp</b>.</p>
          </div>
        )}

        {info && !info.already_active && !info.ok_confirmed && !done && (
          <div className="space-y-4" data-testid="ok-choose">
            <div className="bg-white/10 border border-[#C5A059]/40 rounded-2xl p-5 text-center">
              <p className="text-white text-lg font-bold">Olá, {info.owner_first_name}! 👋</p>
              <p className="text-slate-300 text-sm mt-1">
                Quer continuar com o <b className="text-white">{info.company_name}</b> no Falo com o Dono?
                É só escolher o plano e dar o OK — o boleto chega no seu WhatsApp.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2" data-testid="ok-plans">
              {info.plans.map(p => (
                <button key={p.id} type="button" data-testid={`ok-plan-${p.id}`}
                  onClick={() => setPlanId(p.id)}
                  className={`relative p-3 rounded-xl border text-center transition-colors ${planId === p.id ? 'bg-[#C5A059] text-[#0A2647] border-[#C5A059]' : 'bg-white/5 text-slate-300 border-white/15 hover:border-[#C5A059]/60'}`}>
                  {p.id === info.recommended_plan && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-white text-[#0A2647] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow">{info.current_plan === p.id ? 'SEU PLANO' : 'RECOMENDADO'}</span>}
                  <p className="text-sm font-bold">{p.name}</p>
                  <p className="text-xs opacity-90">
                    {fmt(priceOf(p))}/mês
                    {p.custom_price != null && <span className="block text-[9px] font-bold">preço especial p/ você</span>}
                  </p>
                </button>
              ))}
            </div>

            {!info.has_cpf && (
              <input required placeholder="Seu CPF ou CNPJ (para o boleto)" inputMode="numeric" value={cpf} data-testid="ok-cpf-input"
                onChange={e => setCpf(e.target.value)} className={inputCls} />
            )}

            {error && <p className="text-red-400 text-sm text-center" data-testid="ok-confirm-error">{error}</p>}

            <button onClick={confirm} disabled={loading || !planId} data-testid="ok-confirm-btn"
              className="w-full bg-[#C5A059] text-[#0A2647] font-bold text-lg py-4 rounded-xl hover:bg-[#b8934d] transition-colors disabled:opacity-60">
              {loading ? 'Gerando seu boleto...' : `OK, continuar por ${selectedPlan ? fmt(priceOf(selectedPlan)) : ''}/mês ✅`}
            </button>
            <p className="text-slate-400 text-xs text-center">Boleto vence em 3 dias · na página do boleto você também paga com PIX ou cartão.<br />Sem fidelidade. Cancele quando quiser.</p>
          </div>
        )}

        {done && (
          <div className="bg-white/10 border border-[#C5A059]/40 rounded-2xl p-8 text-center" data-testid="ok-success">
            <p className="text-5xl mb-4">🎉</p>
            <h2 className="text-xl font-bold text-white mb-2">Plano {done.plan_name} ativado!</h2>
            <p className="text-slate-300 text-sm leading-relaxed">
              {fmt(done.final_price)}/mês · boleto vence em <b className="text-white">{done.due_date}</b>.<br />
              O boleto também chegou no seu <b className="text-[#C5A059]">WhatsApp</b>.
            </p>
            {done.payment_url && (
              <a href={done.payment_url} target="_blank" rel="noreferrer" data-testid="ok-payment-link"
                className="inline-block mt-4 bg-[#C5A059] text-[#0A2647] font-bold px-6 py-3 rounded-xl">Ver boleto agora</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
